const STORAGE_KEY = 'brix.salesTable';

export type SortDir = 'asc' | 'desc';

export type SalesSortField =
  | 'number'
  | 'txn_date'
  | 'customer_name'
  | 'total_amount'
  | 'balance'
  | 'status';

export const SALES_SORT_FIELDS: readonly SalesSortField[] = [
  'number',
  'txn_date',
  'customer_name',
  'total_amount',
  'balance',
  'status',
] as const;

const SALES_SORT_FIELD_SET = new Set<string>(SALES_SORT_FIELDS);

export type SalesTablePreferences = {
  sort: SalesSortField | `-${SalesSortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: SalesTablePreferences = {
  sort: '-txn_date',
  perPage: 100,
};

export const encodeSalesSort = (
  field: SalesSortField,
  direction: SortDir
): SalesTablePreferences['sort'] => (direction === 'desc' ? `-${field}` : field);

export const parseSalesSort = (
  value: string
): { field: SalesSortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as SalesSortField;

  if (!SALES_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadSalesTablePreferences = (): SalesTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<SalesTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parseSalesSort(parsed.sort)
        ? (parsed.sort as SalesTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const saveSalesTablePreferences = (prefs: SalesTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
